// Platform Admin — Phase 2 support tickets (ADMIN_UPGRADE_PLAN feature 47).
// Replaces the mocked table with the real cross-tenant queue plus a reply
// thread. Open tickets sort first; replying marks the ticket answered.
import { useCallback, useEffect, useRef, useState } from 'react';
import { useI18n } from '../../lib/i18n';
import { cn } from '../../lib/cn';
import {
  listTickets, getTicket, replyTicket, setTicketStatus,
  type AdminTicketRow, type TicketReply, type TicketStatus, type TicketPriority,
} from '../../lib/padminMoney';

const PILL: Record<TicketStatus, string> = {
  open: 'bg-amber-100 text-amber-700',
  answered: 'bg-green-100 text-green-700',
  closed: 'bg-slate-100 text-ink-2',
};
const DOT: Record<TicketPriority, string> = {
  high: 'bg-red-500', normal: 'bg-blue-500', low: 'bg-ink-3',
};
const GHOST = 'rounded-[8px] border border-slate-200 bg-white px-3 py-1.5 text-xs font-bold text-ink-2 hover:border-primary hover:text-primary';
const BTN = 'rounded-[9px] bg-primary px-4 py-2 text-[13px] font-bold text-white disabled:opacity-40';

function ago(ms: number): string {
  if (!ms) return '—';
  const s = Math.floor((Date.now() - ms) / 1000);
  if (s < 60) return `${s}s`;
  if (s < 3600) return `${Math.floor(s / 60)}m`;
  if (s < 86400) return `${Math.floor(s / 3600)}h`;
  return `${Math.floor(s / 86400)}d`;
}

function Thread({ row, onClose, onChanged }: { row: AdminTicketRow; onClose: () => void; onChanged: () => void }) {
  const { t } = useI18n();
  const [replies, setReplies] = useState<TicketReply[] | null>(null);
  const [body, setBody] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [status, setStatus] = useState<TicketStatus>(row.status);
  const [priority, setPriority] = useState<TicketPriority>(row.priority);

  // Escape closes the thread — a modal you can only dismiss with the mouse is
  // a small daily annoyance for an operator living in this screen.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      // Don't discard a half-written reply, and don't hijack Escape used to
      // dismiss the priority dropdown.
      if (e.key !== 'Escape') return;
      if (bodyRef.current.trim()) return;
      const tag = (e.target as HTMLElement | null)?.tagName;
      if (tag === 'SELECT' || tag === 'TEXTAREA') return;
      onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  // Ref mirror of the draft so the Escape handler doesn't need `body` in deps
  // (which would re-register the listener on every keystroke).
  const bodyRef = useRef('');
  bodyRef.current = body;

  const load = useCallback(() => {
    getTicket(row.tenantId, row.id)
      .then((r) => { setReplies(r.replies); setStatus(r.ticket.status); setPriority(r.ticket.priority); })
      .catch((e: Error) => setErr(e.message));
  }, [row.tenantId, row.id]);
  useEffect(load, [load]);

  async function send(close: boolean) {
    if (body.trim().length === 0) return;
    setBusy(true); setErr(null);
    try {
      await replyTicket({ tenantId: row.tenantId, ticketId: row.id, body: body.trim(), close });
      setBody(''); load(); onChanged();
    } catch (e) { setErr((e as Error).message); } finally { setBusy(false); }
  }

  async function flip(next: TicketStatus) {
    setBusy(true); setErr(null);
    try {
      await setTicketStatus({ tenantId: row.tenantId, ticketId: row.id, status: next });
      setStatus(next); onChanged();
    } catch (e) { setErr((e as Error).message); } finally { setBusy(false); }
  }

  async function changePriority(next: TicketPriority) {
    setBusy(true); setErr(null);
    try {
      // Priority only — sending `status` too could overwrite a status the
      // tenant changed since this panel last loaded.
      await setTicketStatus({ tenantId: row.tenantId, ticketId: row.id, priority: next });
      setPriority(next); onChanged();
    } catch (e) { setErr((e as Error).message); } finally { setBusy(false); }
  }

  return (
    <div className="fixed inset-0 z-40 flex" role="dialog" aria-modal="true">
      <button type="button" aria-label="close" onClick={onClose} className="flex-1 bg-black/40" />
      <aside className="flex h-full w-full max-w-[560px] flex-col bg-white shadow-2xl">
        <header className="flex items-start gap-3 border-b border-slate-200 px-5 py-4">
          <div className="min-w-0 flex-1">
            <b className="block truncate text-sm text-ink">{row.subject}</b>
            <span className="mt-0.5 block text-xs text-ink-3">
              {row.tenantName} · {t('pa.tk.module')}: {row.module} · {row.openedByEmail ?? '—'}
            </span>
          </div>
          <select value={priority} disabled={busy}
            onChange={(e) => void changePriority(e.target.value as TicketPriority)}
            aria-label={t('pa.tk.col.priority')}
            className="rounded-[8px] border border-slate-200 px-2 py-1 text-[11px] font-bold text-ink-2">
            <option value="low">{t('pa.priority.low')}</option>
            <option value="normal">{t('pa.priority.normal')}</option>
            <option value="high">{t('pa.priority.high')}</option>
          </select>
          <span className={cn('rounded-full px-2.5 py-0.5 text-[11px] font-bold', PILL[status])}>
            {t(`pa.tkstatus.${status}`)}
          </span>
          <button type="button" onClick={onClose} className="text-lg leading-none text-ink-3 hover:text-ink">✕</button>
        </header>

        <div className="flex-1 space-y-3 overflow-y-auto bg-surface px-5 py-4">
          {replies === null && <div className="py-8 text-center text-sm text-ink-3">…</div>}
          {replies?.map((r) => (
            <div key={r.id} className={cn('max-w-[85%] rounded-card px-3.5 py-2.5 shadow-sm',
              r.authorRole === 'staff' ? 'ms-auto bg-primary text-white' : 'bg-white')}>
              <span className={cn('block text-[10px] font-bold uppercase tracking-wide',
                r.authorRole === 'staff' ? 'text-white/70' : 'text-ink-3')}>
                {r.authorRole === 'staff' ? t('pa.tk.staff') : t('pa.tk.tenant')} · {ago(r.createdAt)}
              </span>
              <p className={cn('mt-1 whitespace-pre-wrap text-[13px]', r.authorRole === 'staff' ? 'text-white' : 'text-ink')}>
                {r.body}
              </p>
            </div>
          ))}
        </div>

        <footer className="space-y-2 border-t border-slate-200 px-5 py-4">
          {err && <div className="rounded-[9px] bg-red-50 px-3 py-2 text-xs font-semibold text-red-600">{err}</div>}
          <textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            rows={3}
            placeholder={t('pa.tk.replyPlaceholder')}
            className="w-full resize-y rounded-[9px] border border-slate-200 px-3 py-2 text-sm outline-none focus:border-primary"
          />
          <div className="flex flex-wrap gap-2">
            <button type="button" onClick={() => void send(false)} disabled={busy || !body.trim()} className={BTN}>
              {busy ? '…' : t('pa.tk.send')}
            </button>
            <button type="button" onClick={() => void send(true)} disabled={busy || !body.trim()} className={GHOST}>
              {t('pa.tk.sendClose')}
            </button>
            <button
              type="button"
              onClick={() => void flip(status === 'closed' ? 'open' : 'closed')}
              disabled={busy}
              className={cn(GHOST, 'ms-auto')}
            >
              {status === 'closed' ? t('pa.tk.reopen') : t('pa.tk.close')}
            </button>
          </div>
        </footer>
      </aside>
    </div>
  );
}

export default function TicketsPanel() {
  const { t } = useI18n();
  const [rows, setRows] = useState<AdminTicketRow[] | null>(null);
  const [filter, setFilter] = useState<'' | TicketStatus>('');
  const [open, setOpen] = useState<AdminTicketRow | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const load = useCallback(() => {
    listTickets(filter || undefined, 200).then(setRows).catch((e: Error) => setErr(e.message));
  }, [filter]);
  useEffect(load, [load]);

  const TABS: { k: '' | TicketStatus; label: string }[] = [
    { k: '', label: t('pa.tk.filterAll') },
    { k: 'open', label: t('pa.tkstatus.open') },
    { k: 'answered', label: t('pa.tkstatus.answered') },
    { k: 'closed', label: t('pa.tkstatus.closed') },
  ];

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap justify-end gap-1">
        {TABS.map((tab) => (
          <button
            key={tab.k || 'all'}
            type="button"
            onClick={() => setFilter(tab.k)}
            className={cn('rounded-full px-3 py-1 text-xs font-bold',
              filter === tab.k ? 'bg-ink text-white' : 'border border-slate-200 bg-white text-ink-3 hover:text-ink')}
          >
            {tab.label}
          </button>
        ))}
      </div>
      {err && <div className="rounded-[9px] bg-red-50 px-3 py-2 text-xs font-semibold text-red-600">{err}</div>}

      <div className="rounded-card border border-slate-200 bg-white shadow-sm">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[760px] border-collapse">
            <thead>
              <tr>
                {['pa.tk.col.tenant', 'pa.tk.col.subject', 'pa.tk.col.priority', 'pa.tk.col.status', 'pa.tk.col.updated', 'pa.t.col.actions'].map((k) => (
                  <th key={k} className="whitespace-nowrap bg-surface px-3.5 py-3 text-start text-[11px] font-bold uppercase tracking-wide text-ink-3">
                    {t(k)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows === null && <tr><td colSpan={6} className="px-3.5 py-8 text-center text-sm text-ink-3">…</td></tr>}
              {rows?.length === 0 && <tr><td colSpan={6} className="px-3.5 py-8 text-center text-sm text-ink-3">{t('pa.tk.empty')}</td></tr>}
              {rows?.map((tk) => (
                <tr key={`${tk.tenantId}/${tk.id}`} className="border-t border-slate-200">
                  <td className="whitespace-nowrap px-3.5 py-3 text-[13px] font-semibold text-ink">{tk.tenantName}</td>
                  <td className="px-3.5 py-3 text-[13px] text-ink-2">
                    {tk.subject}
                    {tk.lastReplyBy === 'tenant' && tk.status !== 'closed' && (
                      <span className="ms-2 rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-bold text-amber-700">•</span>
                    )}
                  </td>
                  <td className="px-3.5 py-3 text-[13px]">
                    <span className="inline-flex items-center gap-1.5 font-semibold">
                      <span className={cn('h-2 w-2 rounded-full', DOT[tk.priority])} aria-hidden />
                      {t(`pa.priority.${tk.priority}`)}
                    </span>
                  </td>
                  <td className="px-3.5 py-3">
                    <span className={cn('rounded-full px-2.5 py-0.5 text-[11px] font-bold', PILL[tk.status])}>
                      {t(`pa.tkstatus.${tk.status}`)}
                    </span>
                  </td>
                  <td className="whitespace-nowrap px-3.5 py-3 text-xs text-ink-3">{ago(tk.updatedAt)}</td>
                  <td className="whitespace-nowrap px-3.5 py-3">
                    <button type="button" onClick={() => setOpen(tk)} className={GHOST}>{t('pa.action.open')}</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* key remounts the thread per ticket: no stale replies from the previous
          one, and a slow fetch for ticket A can't land on top of ticket B. */}
      {open && <Thread key={`${open.tenantId}/${open.id}`} row={open} onClose={() => setOpen(null)} onChanged={load} />}
    </div>
  );
}
